import type { ReactNode } from "react"
import { cn } from "@/shared/lib/utils"
import { PageTransition } from "@/shared/ui/page-transition"

interface PageShellProps {
	/** Надзаголовок: раздел, к которому относится страница. */
	eyebrow?: string
	title: ReactNode
	subtitle?: string
	/** Главное действие страницы. Стоит в шапке, а не отдельной строкой над списком. */
	action?: ReactNode
	/** «narrow» для форм: длинную строку ввода неудобно читать во всю ширину. */
	width?: "wide" | "narrow"
	children: ReactNode
	className?: string
}

/**
 * Каркас страницы панели. Свечение под шапкой и надзаголовок повторяют мини-апп,
 * чтобы панель и приложение читались как один продукт, а ширина берётся под
 * списки: панель смотрят на большом экране, и колонка в 768 px оставляла треть
 * монитора пустой.
 */
export function PageShell({
	eyebrow,
	title,
	subtitle,
	action,
	width = "wide",
	children,
	className,
}: PageShellProps) {
	return (
		<PageTransition>
			<div className="relative isolate">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_16%,transparent),transparent_70%)]"
				/>
				<div
					className={cn(
						"container mx-auto px-3 py-5 sm:px-6 lg:py-8",
						width === "narrow" ? "max-w-3xl" : "max-w-6xl",
						className,
					)}
				>
					<header className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 lg:mb-8">
						<div className="min-w-0">
							{eyebrow ? (
								<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
									{eyebrow}
								</p>
							) : null}
							<h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
							{subtitle ? (
								<p className="mt-2 text-sm leading-snug text-muted-foreground">{subtitle}</p>
							) : null}
						</div>
						{action ? <div className="shrink-0">{action}</div> : null}
					</header>
					{children}
				</div>
			</div>
		</PageTransition>
	)
}
